import React from 'react';
import PerformanceMetricInputCard from './PerformanceMetricInputCard';
import { Collapse } from '@mui/material';
import {
  MEASUREMENT_CALCULATIONS,
  QUALITY_DOMAINS,
  TIME_BOUNDS
} from '../../../shared/constants/checkpoint-performance-report-metric-enums';
import PerformanceMetricPageInfoBox from './PerformanceMetricPageInfoBox';
import cx from 'classnames';
import Asterisk from '../../../shared/components/Asterisk';
import DoubledAsterisk from '../../../shared/components/DoubledAsterisk';

/**
 * @typedef {{ [field: string]: string | number }} PerformanceMetricFieldChange
 */

/**
 * @typedef {object} PerformanceMetricPageAddControlsProps
 * @property {Object} [pageSettings]
 * @property {boolean} [showDescriptions]
 * @property {Object} [validationErrors]
 * @property {Object} [selectedMetricProperty]
 * @property {(field: string) => void} [handleOnClickMetricField]
 * @property {(fieldChange: PerformanceMetricFieldChange) => void} [handleOnChange]
 * @property {(fieldChange: PerformanceMetricFieldChange) => void} [handleOnChangeActual]
 * @property {(fieldChange: PerformanceMetricFieldChange) => void} [handleOnChangeTimeBounds]
 * @property {(fieldChange: PerformanceMetricFieldChange) => void} [handleOnChangeQualityDomain]
 * @property {(fieldChange: PerformanceMetricFieldChange) => void} [handleOnChangeLowerUpperLimit]
 * @property {(fieldChange: PerformanceMetricFieldChange) => void} [handleOnChangeMeasurementCalculation]
 * @property {Object} [performanceMetric]
 */

/**
 * @param {PerformanceMetricPageAddControlsProps} props
 */
const PerformanceMetricPageAddControls = props => {
  const {
    showDescriptions,
    validationErrors,
    pageSettings,
    selectedMetricProperty,
    handleOnClickMetricField,
    handleOnChange,
    handleOnChangeActual,
    handleOnChangeTimeBounds,
    handleOnChangeQualityDomain,
    handleOnChangeLowerUpperLimit,
    handleOnChangeMeasurementCalculation,
    performanceMetric
  } = props;

  const customOnChangeFunctions = {
    measurementCalculation: handleOnChangeMeasurementCalculation,
    upperLimit: handleOnChangeLowerUpperLimit,
    lowerLimit: handleOnChangeLowerUpperLimit,
    timeBounds: handleOnChangeTimeBounds,
    qualityDomain: handleOnChangeQualityDomain,
    actual: handleOnChangeActual
  };

  const getFieldRenderCondition = field => {
    switch (field) {
      case 'status':
        return false;
      case 'measurementUnit':
        return performanceMetric.measurementCalculation === MEASUREMENT_CALCULATIONS.TotalCount.Id;
      case 'numerator':
      case 'denominator':
        return (
          performanceMetric.measurementCalculation &&
          performanceMetric.measurementCalculation !== MEASUREMENT_CALCULATIONS.TotalCount.Id
        );
      case 'timeFrame':
        return (
          performanceMetric.timeBounds && performanceMetric.timeBounds !== TIME_BOUNDS.AllTime.Id
        );
      case 'customQualityDomainDefinition':
        return performanceMetric.qualityDomain === QUALITY_DOMAINS.NA.Id;
      case 'qualityDomainDefinition':
        return (
          performanceMetric.qualityDomain &&
          performanceMetric.qualityDomain !== QUALITY_DOMAINS.NA.Id
        );
      default:
        return true;
    }
  };

  return (
    <div className='performance-metric-controls'>
      <PerformanceMetricPageInfoBox
        metricPropertyInfo={selectedMetricProperty}
        showDescriptions={showDescriptions}
        validationErrors={validationErrors}
      />
      <div
        className={cx('input-section', { centered: showDescriptions })}
        data-testid='input-fields'>
        {pageSettings.metricProperties.map(fields => {
          const customOnChange = customOnChangeFunctions[fields.field];

          if (
            fields.field == 'qualityDomainDefinition' &&
            performanceMetric.qualityDomain &&
            performanceMetric.qualityDomain !== QUALITY_DOMAINS.NA.Id
          ) {
            return (
              <PerformanceMetricInputCard
                {...fields}
                key={`card-${fields.field}`}
                handleOnClickMetricField={handleOnClickMetricField}
                disabled={true}
                value={
                  Object.values(QUALITY_DOMAINS).find(x => x.Id === performanceMetric.qualityDomain)
                    .Description
                }
              />
            );
          }

          return (
            <Collapse
              key={`card-${fields.field}`}
              in={getFieldRenderCondition(fields.field)}
              unmountOnExit>
              <PerformanceMetricInputCard
                {...fields}
                errors={validationErrors}
                value={performanceMetric[fields.field]}
                onChange={customOnChange || handleOnChange}
                handleOnClickMetricField={handleOnClickMetricField}
                titleAsterisk={fields.addDoubleTitleAsterisk ? <DoubledAsterisk /> : <Asterisk />}
              />
            </Collapse>
          );
        })}
      </div>
    </div>
  );
};

export default PerformanceMetricPageAddControls;
